import type { ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import { useT } from '../../../../app/di';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, FormTextField, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { passwordsDiffer, type Registration } from '../model/registration';

export interface RegisterFormProps {
  onSubmit(registration: Registration): Promise<ServerOutcome>;
}

interface RegisterValues extends Registration {
  passwordRepeat: string;
}

/** IAM-UC-14, IAM-UC-14.CC-2: логин, имя и пароль дважды; ошибки сервера — у своих полей. */
export function RegisterForm({ onSubmit }: RegisterFormProps): ReactElement {
  const { t } = useT();
  const { form, submit } = useServerForm<RegisterValues>({
    defaultValues: { login: '', displayName: '', password: '', passwordRepeat: '' },
    fields: ['login', 'displayName', 'password'],
    onSubmit: ({ login, displayName, password }) => onSubmit({ login, displayName, password }),
  });
  const { control, formState } = form;
  const required = { required: ErrorCode.FieldRequired };
  const disabled = formState.isSubmitting;
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      <FormAlert form={form} />
      <FormTextField control={control} name="login" label={t('identity.register.login')} hint={t('identity.register.loginHint')} autoComplete="username" rules={required} required disabled={disabled} />
      <FormTextField control={control} name="displayName" label={t('identity.register.displayName')} autoComplete="name" rules={required} required disabled={disabled} />
      <FormTextField
        control={control}
        name="password"
        label={t('identity.register.password')}
        hint={t('identity.register.passwordHint')}
        type="password"
        autoComplete="new-password"
        rules={required}
        required
        disabled={disabled}
      />
      <FormTextField
        control={control}
        name="passwordRepeat"
        label={t('identity.register.passwordRepeat')}
        type="password"
        autoComplete="new-password"
        rules={{ ...required, validate: (value: unknown, values: RegisterValues) => (passwordsDiffer(values.password, typeof value === 'string' ? value : '') ? ErrorCode.PasswordMismatch : true) }}
        required
        disabled={disabled}
      />
      <Button type="submit" variant="primary" loading={formState.isSubmitting} block>
        {t('identity.register.submit')}
      </Button>
    </form>
  );
}
